import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Plus, FolderHeart, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORG_TYPES = { porteur: 'Porteur de projet', entreprise: 'Entreprise', association: 'Association', dirigeant: 'Dirigeant' };
const STATUTS = { actif: 'bg-emerald-50 text-emerald-700 border-emerald-200', pause: 'bg-amber-50 text-amber-700 border-amber-200', termine: 'bg-stone-100 text-stone-600 border-stone-200' };

export default function AdrnsDossiers() {
    const { session } = useAuth();
    const [items, setItems] = useState(null);
    const [tab, setTab] = useState('actifs'); // actifs | archives
    const [open, setOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null); // dossier à supprimer définitivement
    const [form, setForm] = useState({ porteur_name: '', porteur_email: '', porteur_phone: '', org_type: 'porteur', besoin: '' });
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const hdrs = () => ({ headers: { Authorization: `Bearer ${session.access_token}` } });

    const reload = (t = tab) => axios.get(`${API}/adrns/dossiers${t === 'archives' ? '?archived=true' : ''}`, hdrs())
        .then(r => setItems(r.data.items)).catch(() => setItems([]));
    useEffect(() => { if (session) { setItems(null); reload(tab); } /* eslint-disable-next-line */ }, [session, tab]);

    const create = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}/adrns/dossiers`, form, hdrs());
            toast.success(form.porteur_email
                ? 'Dossier créé — les accès du porteur lui seront envoyés par email'
                : 'Dossier créé');
            setOpen(false);
            setForm({ porteur_name: '', porteur_email: '', porteur_phone: '', org_type: 'porteur', besoin: '' });
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setSaving(false);
    };

    const archive = async (d) => {
        setBusyId(d.id);
        try {
            await axios.delete(`${API}/adrns/dossiers/${d.id}`, hdrs());
            toast.success('Dossier archivé — retrouvez-le dans l\'onglet Archivés');
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusyId(null);
    };

    const restore = async (d) => {
        setBusyId(d.id);
        try {
            await axios.post(`${API}/adrns/dossiers/${d.id}/restore`, {}, hdrs());
            toast.success('Dossier restauré');
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusyId(null);
    };

    const deletePermanent = async () => {
        const d = confirmDelete;
        setBusyId(d.id);
        try {
            await axios.delete(`${API}/adrns/dossiers/${d.id}/permanent`, hdrs());
            toast.success('Dossier supprimé définitivement');
            setConfirmDelete(null);
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusyId(null);
    };

    return (
        <div className="space-y-6" data-testid="adrns-dossiers-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow mb-2">ADRNS · Dossiers</div>
                    <h1 className="font-heading text-4xl">Dossiers d'accompagnement</h1>
                </div>
                <Button onClick={() => setOpen(true)} className="bg-primary hover:bg-primary-hover" data-testid="adrns-new-dossier-btn">
                    <Plus size={15} className="mr-1.5" /> Nouveau dossier
                </Button>
            </div>

            <div className="flex gap-1 border-b border-border" data-testid="adrns-dossiers-tabs">
                <button onClick={() => setTab('actifs')}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'actifs' ? 'border-primary text-primary' : 'border-transparent text-foreground/60 hover:text-foreground'}`}
                    data-testid="adrns-tab-actifs">
                    Dossiers actifs
                </button>
                <button onClick={() => setTab('archives')}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${tab === 'archives' ? 'border-primary text-primary' : 'border-transparent text-foreground/60 hover:text-foreground'}`}
                    data-testid="adrns-tab-archives">
                    <Archive size={13} /> Archivés
                </button>
            </div>

            {!items ? (
                <div className="flex justify-center py-20"><Loader2 className="animate-spin" /></div>
            ) : items.length === 0 ? (
                <div className="card-soft p-12 text-center text-foreground/60" data-testid="adrns-dossiers-empty">
                    <FolderHeart size={30} className="mx-auto mb-3 text-muted-foreground" />
                    {tab === 'archives' ? 'Aucun dossier archivé.' : 'Aucun dossier. Créez le premier dossier d\'accompagnement.'}
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-4">
                    {items.map(d => (
                        <div key={d.id} className={`card-soft p-5 hover:shadow-md transition-shadow relative ${tab === 'archives' ? 'opacity-80' : ''}`} data-testid={`adrns-dossier-${d.id}`}>
                            <Link to={`/app/adrns/dossiers/${d.id}`} className="block">
                                <div className="flex items-start justify-between gap-3 pr-8">
                                    <div>
                                        <div className="font-heading text-lg">{d.porteur_name}</div>
                                        <div className="text-xs uppercase tracking-wider text-muted-foreground mt-0.5">{ORG_TYPES[d.org_type] || d.org_type}</div>
                                    </div>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${tab === 'archives' ? 'bg-stone-100 text-stone-500 border-stone-200' : (STATUTS[d.statut] || STATUTS.actif)}`}>
                                        {tab === 'archives' ? 'archivé' : (d.statut || 'actif')}
                                    </span>
                                </div>
                                {d.besoin && <p className="text-sm text-foreground/70 mt-3 line-clamp-2">{d.besoin}</p>}
                                <div className="text-xs text-muted-foreground mt-3">
                                    {Object.values(d.modules_actifs || {}).filter(Boolean).length}/6 modules actifs · créé le {new Date(d.created_at).toLocaleDateString('fr-FR')}
                                    {tab === 'archives' && d.archived_at && <> · archivé le {new Date(d.archived_at).toLocaleDateString('fr-FR')}</>}
                                </div>
                            </Link>
                            <div className="absolute top-4 right-4 flex flex-col gap-1.5">
                                {tab === 'actifs' ? (
                                    <button title="Archiver ce dossier" disabled={busyId === d.id}
                                        onClick={() => archive(d)}
                                        className="h-7 w-7 rounded-md flex items-center justify-center text-foreground/40 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                                        data-testid={`adrns-archive-btn-${d.id}`}>
                                        {busyId === d.id ? <Loader2 size={14} className="animate-spin" /> : <Archive size={14} />}
                                    </button>
                                ) : (
                                    <>
                                        <button title="Restaurer ce dossier" disabled={busyId === d.id}
                                            onClick={() => restore(d)}
                                            className="h-7 w-7 rounded-md flex items-center justify-center text-foreground/40 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                                            data-testid={`adrns-restore-btn-${d.id}`}>
                                            {busyId === d.id ? <Loader2 size={14} className="animate-spin" /> : <ArchiveRestore size={14} />}
                                        </button>
                                        <button title="Supprimer définitivement" disabled={busyId === d.id}
                                            onClick={() => setConfirmDelete(d)}
                                            className="h-7 w-7 rounded-md flex items-center justify-center text-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors"
                                            data-testid={`adrns-delete-btn-${d.id}`}>
                                            <Trash2 size={14} />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <DialogContent className="sm:max-w-md" data-testid="adrns-delete-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Supprimer définitivement ?</DialogTitle>
                        <DialogDescription>
                            Le dossier <strong>{confirmDelete?.porteur_name}</strong> et toutes ses données
                            (livrables, interventions, documents, missions) seront supprimés définitivement.
                            Cette action est irréversible.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annuler</Button>
                        <Button variant="destructive" onClick={deletePermanent} disabled={busyId === confirmDelete?.id}
                            data-testid="adrns-delete-confirm-btn">
                            {busyId === confirmDelete?.id ? <Loader2 size={14} className="animate-spin" /> : 'Supprimer définitivement'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-md" aria-describedby={undefined} data-testid="adrns-new-dossier-dialog">
                    <DialogHeader><DialogTitle className="font-heading">Nouveau dossier d'accompagnement</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <Input placeholder="Nom du porteur / dirigeant *" value={form.porteur_name}
                            onChange={e => setForm(f => ({ ...f, porteur_name: e.target.value }))} data-testid="adrns-form-name" />
                        <div>
                            <Input placeholder="Email du porteur" type="email" value={form.porteur_email}
                                onChange={e => setForm(f => ({ ...f, porteur_email: e.target.value }))} data-testid="adrns-form-email" />
                            <p className="text-[11px] text-muted-foreground mt-1">
                                Un compte sera créé automatiquement pour le porteur : il recevra ses accès par email.
                            </p>
                        </div>
                        <Input placeholder="Téléphone" value={form.porteur_phone}
                            onChange={e => setForm(f => ({ ...f, porteur_phone: e.target.value }))} />
                        <select value={form.org_type} onChange={e => setForm(f => ({ ...f, org_type: e.target.value }))}
                            className="h-10 w-full px-3 rounded-md border border-input bg-background text-sm" data-testid="adrns-form-type">
                            {Object.entries(ORG_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <Textarea placeholder="Besoin exprimé / contexte" rows={3} value={form.besoin}
                            onChange={e => setForm(f => ({ ...f, besoin: e.target.value }))} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                        <Button onClick={create} disabled={saving || form.porteur_name.trim().length < 2}
                            className="bg-primary hover:bg-primary-hover" data-testid="adrns-form-submit">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : 'Créer le dossier'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
